import React, { useState, useEffect, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { mailingListSignup } from "../../../util/adminApi";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { RECAPTCHA_SITEKEY } from "../../../config";
import {
  LeftDiv,
  SpaceApartDiv,
} from "../../layout/restaurantBuilder/restaurantBuilderLegacy/RestaurantCreateBuild";

const SignupWidget = ({ setAlert, location, locationState, ...props }) => {
  const [userInfo, setUserInfo] = useState({});
  const [registered, setRegistered] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [followCity, setFollowCity] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [selectedCity, setSelectedCity] = useState(null);

  const cityInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  const placesLib = useMapsLibrary("places");

  const { email, name } = userInfo;

  // Initialize Google Places Autocomplete on the city input
  useEffect(function initGooglePlacesAutocomplete() {
    if (!placesLib || !cityInputRef.current || autocompleteRef.current) return;

    const autocomplete = new placesLib.Autocomplete(
      cityInputRef.current,
      {
        types: ["(cities)"],
        componentRestrictions: { country: "us" },
      }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      let cityName = "";
      let state = "";
      for (const component of place.address_components || []) {
        if (component.types.includes("locality")) {
          cityName = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }

      setSelectedCity({ cityName, state, lat, lng });
    });

    autocompleteRef.current = autocomplete;

    // Pre-fill with detected city if available
    if (location?.locationDisplay?.name && cityInputRef.current) {
      cityInputRef.current.value = location.locationDisplay.name;
    }
  }, [placesLib]); // eslint-disable-line react-hooks/exhaustive-deps

  const register = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      setAlert("Please complete recaptcha", "failure");
      return;
    }

    const cityName = selectedCity?.cityName || cityInputRef.current?.value || "";
    const cityState = selectedCity?.state || locationState;
    const cityLat = selectedCity?.lat || location?.lat;
    const cityLng = selectedCity?.lng || location?.lng;

    const payload = {
      email,
      name,
      city: cityName,
      location: { lat: cityLat, lng: cityLng },
      state: cityState,
      recaptchaToken,
    };

    if (followCity && cityName && cityLat && cityLng) {
      payload.followCityPreferences = {
        cityName,
        state: cityState,
        lat: cityLat,
        lng: cityLng,
        radiusMiles,
      };
    }

    const resp = await mailingListSignup(payload);
    if (resp.response?.status === 400) {
      setAlert(
        resp.response.data.errors.map((obj) => obj.msg).join(", "),
        "failure"
      );
    } else if (resp.response?.status === 500) {
      setAlert(
        "Error when registering, please try again in a few minutes",
        "failure"
      );
    } else {
      setRegistered(true);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    setUserInfo({});
  };

  return (
    <form onSubmit={(e) => register(e)}>
      <div className="form-container">
        <LeftDiv>
          <SpaceApartDiv>
            <label className="form-label-inline">Email Address*</label>
            <input
              type="text"
              className="text-form"
              name="email"
              value={userInfo.email || ""}
              onChange={(e) =>
                setUserInfo({ ...userInfo, email: e.target.value })
              }
              required
            />
          </SpaceApartDiv>
          <SpaceApartDiv>
            <label className="form-label-inline">Your Name</label>
            <input
              type="text"
              className="text-form"
              value={userInfo.name || ""}
              onChange={(e) =>
                setUserInfo({ ...userInfo, name: e.target.value })
              }
            />
          </SpaceApartDiv>
          <SpaceApartDiv>
            <label className="form-label-inline">City</label>
            <input
              ref={cityInputRef}
              type="text"
              className="text-form"
              placeholder="Start typing a city name..."
            />
          </SpaceApartDiv>
          <SpaceApartDiv>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={followCity}
                onChange={(e) => setFollowCity(e.target.checked)}
              />
              Let me know when new vegan restaurants and options are added near me
            </label>
          </SpaceApartDiv>
          {followCity && (
            <SpaceApartDiv>
              <label className="form-label-inline">Notify me within</label>
              <select
                className="text-form"
                value={radiusMiles}
                onChange={(e) => setRadiusMiles(Number(e.target.value))}
              >
                <option value={5}>5 miles</option>
                <option value={10}>10 miles</option>
                <option value={25}>25 miles</option>
              </select>
            </SpaceApartDiv>
          )}
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITEKEY}
            onChange={setRecaptchaToken}
          />
          <input
            style={{
              textAlign: "center",
              alignSelf: "center",
              fontSize: "1.5em",
              height: "revert",
            }}
            className="btn-primary"
            type="submit"
            value={props.callToAction || "Sign Up"}
          />
        </LeftDiv>
        {registered && (
          <SpaceApartDiv>
            <p>
              Thank you for signing up! We'll send you an invitation email
              shortly. Check your inbox (and spam folder) for a message from
              harrison@vegifyit.com.
            </p>
          </SpaceApartDiv>
        )}
      </div>
    </form>
  );
};
const mapStateToProps = (state) => ({
  location: state.currentLocation,
  locationState: state.currentLocation.state || "",
});
export default connect(mapStateToProps, { setAlert })(SignupWidget);
